import {
  UrlParamKey,
  UrlParams,
} from 'app/core/api/api-core/api-constants';
import { MissingParamError } from 'app/core/errors/error-variants';
import { History } from 'history';
import _ from 'lodash';
import {
  useObservableCallback,
  useObservableEagerState,
} from 'observable-hooks';
import {
  DependencyList,
  MutableRefObject,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useIdleTimer } from 'react-idle-timer';
import {
  match,
  useHistory,
  useParams,
  useRouteMatch,
} from 'react-router-dom';
import {
  BehaviorSubject,
  identity,
  isObservable,
  Observable,
} from 'rxjs';
import { SetRequired } from 'type-fest';
import { isNonNullable, Obj, Opt } from '../general.types';
import { newUnit } from '../utils';

export const useEffectOnce = (
  effect: React.EffectCallback
): unknown =>
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(effect, []);

/**
 * Just like useEffect, but it doesn't run the first time the component
 * loads. Instead it only runs the first time the deps actually *change*
 */
export const useEffectOnChanges = (
  effect: React.EffectCallback,
  deps?: DependencyList
): unknown => {
  const firstTime = useRef(true);
  return useEffect(() => {
    const rtn = firstTime.current ? undefined : effect();
    firstTime.current = false;
    return rtn;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};

export const useLayoutEffectOnce = (
  effect: React.EffectCallback
): unknown =>
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(effect, []);

export const usePreviousValue = <
  T,
  I extends T | undefined = undefined
>(
  value: T,
  initialVal?: I
): I extends undefined ? Opt<T> : T => {
  const initialValArgs = (initialVal ? [initialVal] : []) as [T];
  const ref = useRef<T>(...initialValArgs);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
};

export const useWatchAsRef = <T>(value: T): MutableRefObject<T> => {
  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);
  return valueRef;
};

export const useTimeMountedExceeds = (
  ms: number,
  initial = false
): [timeExpired: Opt<true>, resetTimer: () => void] => {
  const initialised = useRef(false);
  const resetInitialised = useRef(true);
  const [timeExpired, setTimeExpired] = useState(initial);
  const [unit, triggerReset] = useReducer(() => {
    resetInitialised.current = false;
    return newUnit();
  }, newUnit());

  useEffect(() => {
    if (!initialised.current) {
      initialised.current = true;
      if (initial) {
        return;
      }
    }

    resetInitialised.current = true;
    setTimeExpired(false);
    const timeout = setTimeout(() => {
      setTimeExpired(true);
    }, ms);

    return () => {
      clearTimeout(timeout);
    };
  }, [initial, ms, unit]);

  const timeExpiredFromReset =
    timeExpired && resetInitialised.current;
  return [timeExpiredFromReset || undefined, triggerReset];
};

export const useFocus = <
  T extends HTMLElement = HTMLElement
>(): readonly [React.RefObject<T>, () => void] => {
  const htmlElRef = useRef<T>(null);
  const setFocus = () => {
    htmlElRef?.current?.focus();
  };

  return [htmlElRef, setFocus] as const;
};

export const useHighlight = (): readonly [
  React.RefObject<HTMLInputElement>,
  () => void
] => {
  const htmlElRef = useRef<HTMLInputElement>(null);
  const setFocus = () => {
    htmlElRef?.current?.select();
  };

  return [htmlElRef, setFocus] as const;
};

export const useObservableSend = <T = void>(): [
  sendData: (valueToSend: T) => void,
  dataEvent$: Observable<T>
] => useObservableCallback<T>(identity);

export const useObservableBinding = <T>(
  // eslint-disable-next-line rxjs/finnish
  input: BehaviorSubject<T> | T
): [state: T, binding$: BehaviorSubject<T>] => {
  const binding$ = useMemo(
    () => (isObservable(input) ? input : new BehaviorSubject(input)),
    [input]
  );
  const state = useObservableEagerState(binding$);
  return useMemo(() => [state, binding$], [binding$, state]);
};

export const useOnUnmount = (func: () => unknown): void => {
  useEffect(
    () => () => {
      func();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
};

export default function useBodyClass(
  className?: string | Opt<string>[]
): void {
  const classArr = _.flattenDeep([className])
    .map(n => n?.split(' '))
    .flat()
    .filter(isNonNullable)
    // remove auto-generated css element component names
    // TODO: [switch off this functionality since it isn't used]
    .filter(n => n && !n.includes('__'));

  useEffect(() => {
    classArr.map((name: string) => document.body.classList.add(name));
    return () => {
      classArr.map((name: string) =>
        document.body.classList.remove(name)
      );
    };
  }, [classArr]);
}

export const usePollInterval = (
  infrequent?: 'infrequent'
): number | null => {
  const multiplier = infrequent ? 2 : 1;
  const fastPollInterval = 3000 * multiplier;
  const slowPollInterval = 6000 * multiplier;

  const [pollInterval, setPollInterval] = useState<number | null>(
    fastPollInterval
  );

  const handleOnActive = () => {
    setPollInterval(fastPollInterval);
  };
  const handleOnIdle30Secs = () => {
    setPollInterval(slowPollInterval);
  };
  const handleOnIdle2Mins = () => {
    setPollInterval(null);
  };

  useIdleTimer({
    timeout: 1000 * 30,
    onIdle: handleOnIdle30Secs,
    onActive: handleOnActive,
    debounce: 500,
  });
  useIdleTimer({
    timeout: 1000 * 60 * 2,
    onIdle: handleOnIdle2Mins,
    onActive: handleOnActive,
    debounce: 500,
  });

  return pollInterval;
};

export const useUrlParams = <K extends `${UrlParamKey}` = never>(
  essentialParams: K[] = []
): SetRequired<UrlParams, K> => {
  const params = useParams<UrlParams>();

  essentialParams.forEach(essentialParamName => {
    if (!isNonNullable(params[essentialParamName])) {
      throw new MissingParamError(essentialParamName);
    }
  });

  // NOTE: this must now be true, due to the above 'forEach'
  return params as SetRequired<UrlParams, K>;
};

export const useUrlRouteMatch = (): match<UrlParams> =>
  useRouteMatch<UrlParams>();

export const useQueryParams = (): URLSearchParams =>
  new URLSearchParams(window.location.search);

export const useComponentIsStillLoaded = (): (() => boolean) => {
  const isLoaded = useRef(true);
  useEffectOnce(() => () => {
    isLoaded.current = false;
  });
  return () => isLoaded.current;
};

/** Stores the current element responsible for page scrolling. */
export const appScrollElement$ =
  new BehaviorSubject<HTMLDivElement | null>(null);
const scrollHistory = {} as Obj<number>;
/**
 * Overrides the PUSH and REPLACE methods in history to save the
 * window scroll position for the route.
 */
export const useInitialiseScrollMemory = (): void => {
  const history = useHistory();

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/unbound-method
    const { push, replace } = history;

    const storeScroll = () => {
      const { scrollTop } = appScrollElement$.value || {};
      const { pathname } = history.location;
      if (typeof scrollTop === 'number') {
        scrollHistory[pathname] = scrollTop;
      }
    };

    // Override the history PUSH method to automatically set scroll state.
    history.push = (...args) => {
      storeScroll();
      // NOTE: necessary due to typescript not handling function overloads well
      push(...(args as Parameters<History['push']>));
    };

    // Override the history REPLACE method to automatically set scroll state.
    history.replace = (...args) => {
      storeScroll();
      replace(...(args as Parameters<History['replace']>));
    };

    // Listen for location changes and set the scroll position accordingly.
    const unregister = history.listen((location, action) => {
      if (action === 'POP') {
        // NOTE: only restores scroll when using back / forward history actions
        const scrollTop = scrollHistory[location.pathname];
        appScrollElement$.value?.scrollTo({
          top: scrollTop || 0,
        });
      } else {
        // otherwise, just scroll to top
        appScrollElement$.value?.scrollTo({ top: 0 });
      }
    });

    // Unregister listener when component unmounts.
    return () => {
      unregister();
    };
  }, [history]);
};
